import type { GetOrganizationsResponse } from '@/lib/api/types/multi-orgs.types';
import { clients } from '@/lib/https';
import { User } from '@/types/user.type';

export const getAccount = async (): Promise<User> => {
  const res = await clients.get<{ data: User }>('/iam/v4/iam/me');
  return res.data;
};

/**
 * Resources of every UI (FrontendAction) permission the signed-in user holds through their roles in
 * the session's organization, from IAM's `GET /iam/v4/iam/resource/features`. IAM reads the user and
 * organization from the access token, so no roles or organization are passed.
 *
 * `/iam/me` returns a `permissions` list that can lag behind what the user's roles actually hold, so
 * this is the source of truth. Callers treat a failure as "no extra permissions", not as an access error.
 */
export const getMyPermissions = async (): Promise<string[]> => {
  const res = await clients.get<{ resource?: string }[]>('/iam/v4/iam/resource/features');
  return (res ?? []).flatMap((item) => (item.resource ? [item.resource] : []));
};

/**
 * Organizations the signed-in user belongs to. Called on the API host directly, like `me`: the call must
 * reach IAM exactly as `me` does, or a token `me` accepts can be refused here.
 */
export const getMyOrganizations = async (): Promise<GetOrganizationsResponse> => {
  return clients.get<GetOrganizationsResponse>('/iam/v4/iam/organizations/my');
};
